import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Filter, Plus, Minus } from "lucide-react";
import StandingsTable from "../../components/StandingsTable";
import { Button, Card, EmptyState, Field, IconButton, Loader, PageHeader, Select, StatCard } from "../../ui";
import "./styles.scss";

interface FastTournamentData {
  teams: string[];
  format: "League" | "Group" | "Knockout";
  generatedData: any;
  createdAt: string;
  numberOfGroups?: number;
}

interface SetScore {
  homeScore: number | "";
  awayScore: number | "";
}

interface MatchScore {
  sets: SetScore[];
}

interface LeagueStanding {
  teamName: string;
  played: number;
  wins: number;
  losses: number;
  setsWon: number;
  setsLost: number;
  pointDifference: number;
  points: number;
}

interface GroupStanding {
  teamName: string;
  played: number;
  wins: number;
  losses: number;
  setsWon: number;
  setsLost: number;
  pointDifference: number;
  points: number;
}

const FastTournamentPage = () => {
  const navigate = useNavigate();
  const [tournamentData, setTournamentData] = useState<FastTournamentData | null>(null);
  const [matchScores, setMatchScores] = useState<{ [key: string]: MatchScore }>({});
  const [filterGroup, setFilterGroup] = useState<string>("all");
  const [filterTeam, setFilterTeam] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [numberOfSets, setNumberOfSets] = useState<number>(1);

  useEffect(() => {
    const storedData = sessionStorage.getItem("fastTournament");
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        setTournamentData(data);
        document.title = "Fast Tournament";

        // Load saved scores if any
        const savedScores = sessionStorage.getItem("fastTournamentScores");
        if (savedScores) {
          setMatchScores(JSON.parse(savedScores));
        }
      } catch (error) {
        console.error("Error parsing fast tournament data:", error);
        navigate("/dashboard/tournaments");
      }
    } else {
      navigate("/dashboard/tournaments");
    }
  }, [navigate]);

  // Save scores to sessionStorage whenever they change
  useEffect(() => {
    if (Object.keys(matchScores).length > 0) {
      sessionStorage.setItem("fastTournamentScores", JSON.stringify(matchScores));
    }
  }, [matchScores]);

  // Update match scores when numberOfSets changes
  useEffect(() => {
    setMatchScores((prev) => {
      const updated: { [key: string]: MatchScore } = {};
      Object.keys(prev).forEach((key) => {
        const existingSets = prev[key].sets || [];
        const newSets = Array(numberOfSets)
          .fill(null)
          .map((_, index) => existingSets[index] || { homeScore: "", awayScore: "" });
        updated[key] = { sets: newSets };
      });
      return updated;
    });
  }, [numberOfSets]);

  const handleScoreChange = (matchIndex: number, setIndex: number, side: "home" | "away", value: string) => {
    const key = `match-${matchIndex}`;
    const numValue = value === "" ? "" : parseInt(value);

    if (numValue !== "" && (numValue < 0 || numValue > 100)) {
      return; // Prevent invalid scores
    }

    setMatchScores((prev) => {
      const currentMatch = prev[key] || { sets: Array(numberOfSets).fill(null).map(() => ({ homeScore: "", awayScore: "" })) };
      const updatedSets = [...currentMatch.sets];
      updatedSets[setIndex] = {
        ...updatedSets[setIndex],
        [side === "home" ? "homeScore" : "awayScore"]: numValue,
      };

      return {
        ...prev,
        [key]: {
          sets: updatedSets,
        },
      };
    });
  };

  const handleAddSet = (matchIndex: number) => {
    const key = `match-${matchIndex}`;
    setMatchScores((prev) => {
      const currentMatch = prev[key] || { sets: [] };
      return {
        ...prev,
        [key]: {
          sets: [...currentMatch.sets, { homeScore: "", awayScore: "" }],
        },
      };
    });
  };

  const handleRemoveSet = (matchIndex: number, setIndex: number) => {
    const key = `match-${matchIndex}`;
    setMatchScores((prev) => {
      const currentMatch = prev[key];
      if (!currentMatch || !currentMatch.sets || currentMatch.sets.length <= 1) {
        return prev; // Don't remove if only one set or no sets
      }
      const updatedSets = currentMatch.sets.filter((_: any, index: number) => index !== setIndex);
      return {
        ...prev,
        [key]: {
          sets: updatedSets,
        },
      };
    });
  };

  // Get unique groups for filters
  const uniqueGroups = useMemo(() => {
    if (!tournamentData?.generatedData?.matches) {
      return [];
    }

    const groups = new Set<string>();

    tournamentData.generatedData.matches.forEach((match: any) => {
      if (match.group) groups.add(match.group);
    });

    return Array.from(groups).sort();
  }, [tournamentData]);

  // Filter matches based on selected filters
  const filteredMatches = useMemo(() => {
    if (!tournamentData?.generatedData?.matches) return [];

    return tournamentData.generatedData.matches.filter((match: any) => {
      if (filterGroup !== "all" && match.group !== filterGroup) return false;
      if (filterTeam !== "all" && match.homeTeam !== filterTeam && match.awayTeam !== filterTeam) return false;
      return true;
    });
  }, [tournamentData, filterGroup, filterTeam]);

  // Calculate standings for League format
  const leagueStandings = useMemo((): LeagueStanding[] => {
    if (!tournamentData || tournamentData.format !== "League" || !tournamentData.generatedData?.matches) {
      return [];
    }

    const standingsMap: { [teamName: string]: LeagueStanding } = {};

    // Initialize all teams
    tournamentData.teams.forEach((team) => {
      standingsMap[team] = {
        teamName: team,
        played: 0,
        wins: 0,
        losses: 0,
        setsWon: 0,
        setsLost: 0,
        pointDifference: 0,
        points: 0,
      };
    });

    // Process matches
    tournamentData.generatedData.matches.forEach((match: any, index: number) => {
      const key = `match-${index}`;
      const score = matchScores[key];

      if (score && score.sets && score.sets.length > 0) {
        let homeSetsWon = 0;
        let awaySetsWon = 0;
        let homeTotalPoints = 0;
        let awayTotalPoints = 0;

        score.sets.forEach((set: SetScore) => {
          if (set.homeScore !== "" && set.awayScore !== "") {
            const homeScore = Number(set.homeScore);
            const awayScore = Number(set.awayScore);

            homeTotalPoints += homeScore;
            awayTotalPoints += awayScore;

            if (homeScore > awayScore) {
              homeSetsWon++;
            } else if (awayScore > homeScore) {
              awaySetsWon++;
            }
          }
        });

        // Only count match if at least one set is completed
        if (homeSetsWon > 0 || awaySetsWon > 0) {
          standingsMap[match.homeTeam].played++;
          standingsMap[match.awayTeam].played++;

          if (homeSetsWon > awaySetsWon) {
            standingsMap[match.homeTeam].wins++;
            standingsMap[match.awayTeam].losses++;
            standingsMap[match.homeTeam].points += 3;
          } else if (awaySetsWon > homeSetsWon) {
            standingsMap[match.awayTeam].wins++;
            standingsMap[match.homeTeam].losses++;
            standingsMap[match.awayTeam].points += 3;
          }

          standingsMap[match.homeTeam].setsWon += homeSetsWon;
          standingsMap[match.homeTeam].setsLost += awaySetsWon;
          standingsMap[match.awayTeam].setsWon += awaySetsWon;
          standingsMap[match.awayTeam].setsLost += homeSetsWon;

          standingsMap[match.homeTeam].pointDifference += homeTotalPoints - awayTotalPoints;
          standingsMap[match.awayTeam].pointDifference += awayTotalPoints - homeTotalPoints;
        }
      }
    });

    return Object.values(standingsMap).sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.setsWon - b.setsLost !== a.setsWon - a.setsLost) {
        return b.setsWon - b.setsLost - (a.setsWon - a.setsLost);
      }
      return b.pointDifference - a.pointDifference;
    });
  }, [tournamentData, matchScores]);

  // Calculate standings for Group format
  const groupStandings = useMemo((): { [groupName: string]: GroupStanding[] } => {
    if (!tournamentData || tournamentData.format !== "Group" || !tournamentData.generatedData?.matches) {
      return {};
    }

    const standingsMap: { [groupName: string]: { [teamName: string]: GroupStanding } } = {};

    // Initialize all teams in their groups
    if (tournamentData.generatedData.groups) {
      Object.entries(tournamentData.generatedData.groups).forEach(([groupName, groupTeams]: [string, any]) => {
        standingsMap[groupName] = {};
        (groupTeams as string[]).forEach((team) => {
          standingsMap[groupName][team] = {
            teamName: team,
            played: 0,
            wins: 0,
            losses: 0,
            setsWon: 0,
            setsLost: 0,
            pointDifference: 0,
            points: 0,
          };
        });
      });
    }

    // Process matches
    tournamentData.generatedData.matches.forEach((match: any, index: number) => {
      if (!match.group) return;

      const key = `match-${index}`;
      const score = matchScores[key];

      if (score && score.sets && score.sets.length > 0) {
        let homeSetsWon = 0;
        let awaySetsWon = 0;
        let homeTotalPoints = 0;
        let awayTotalPoints = 0;

        score.sets.forEach((set: SetScore) => {
          if (set.homeScore !== "" && set.awayScore !== "") {
            const homeScore = Number(set.homeScore);
            const awayScore = Number(set.awayScore);

            homeTotalPoints += homeScore;
            awayTotalPoints += awayScore;

            if (homeScore > awayScore) {
              homeSetsWon++;
            } else if (awayScore > homeScore) {
              awaySetsWon++;
            }
          }
        });

        // Only count match if at least one set is completed
        if (homeSetsWon > 0 || awaySetsWon > 0) {
          const groupStandings = standingsMap[match.group];
          if (!groupStandings) return;

          groupStandings[match.homeTeam].played++;
          groupStandings[match.awayTeam].played++;

          if (homeSetsWon > awaySetsWon) {
            groupStandings[match.homeTeam].wins++;
            groupStandings[match.awayTeam].losses++;
            groupStandings[match.homeTeam].points += 3;
          } else if (awaySetsWon > homeSetsWon) {
            groupStandings[match.awayTeam].wins++;
            groupStandings[match.homeTeam].losses++;
            groupStandings[match.awayTeam].points += 3;
          }

          groupStandings[match.homeTeam].setsWon += homeSetsWon;
          groupStandings[match.homeTeam].setsLost += awaySetsWon;
          groupStandings[match.awayTeam].setsWon += awaySetsWon;
          groupStandings[match.awayTeam].setsLost += homeSetsWon;

          groupStandings[match.homeTeam].pointDifference += homeTotalPoints - awayTotalPoints;
          groupStandings[match.awayTeam].pointDifference += awayTotalPoints - homeTotalPoints;
        }
      }
    });

    // Sort each group's standings
    const sortedStandings: { [groupName: string]: GroupStanding[] } = {};
    Object.entries(standingsMap).forEach(([groupName, standings]) => {
      sortedStandings[groupName] = Object.values(standings).sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.setsWon - b.setsLost !== a.setsWon - a.setsLost) {
          return b.setsWon - b.setsLost - (a.setsWon - a.setsLost);
        }
        return b.pointDifference - a.pointDifference;
      });
    });

    return sortedStandings;
  }, [tournamentData, matchScores]);

  if (!tournamentData) {
    return (
      <div className="fast-tournament-page">
        <Loader minHeight={400} />
      </div>
    );
  }

  const { teams, format, generatedData, numberOfGroups } = tournamentData;

  return (
    <div className="fast-tournament-page">
      <PageHeader
        back={{ onClick: () => navigate("/dashboard/tournaments"), label: "Back to Tournaments" }}
        title="Quick Tournament"
        subtitle="Manage Tournament Matches & Standings"
      />

      <div className="tournament-info">
        <StatCard label="Tournament Format" value={format} tone="spark" />
        <StatCard label="Number of Teams" value={teams.length} tone="brand" />
        {format === "Group" && numberOfGroups && (
          <StatCard label="Number of Groups" value={numberOfGroups} tone="neutral" />
        )}
      </div>

      {/* Standings Section for League and Group formats */}
      {(format === "League" || format === "Group") && (
        <Card className="standings-section" title={format === "League" ? "League Table" : "Group Standings"}>
          {format === "League" ? (
            <StandingsTable>
              {leagueStandings.map((standing, index) => (
                <tr key={standing.teamName}>
                  <td>{index + 1}</td>
                  <td>{standing.teamName}</td>
                  <td>{standing.played}</td>
                  <td>{standing.wins}</td>
                  <td>{standing.losses}</td>
                  <td>
                    {standing.setsWon}:{standing.setsLost}
                  </td>
                  <td>{standing.pointDifference}</td>
                  <td>{standing.points}</td>
                </tr>
              ))}
            </StandingsTable>
          ) : (
            <div className="group-standings-container">
              {Object.entries(groupStandings).map(([groupName, standings]) => (
                <div key={groupName} className="group-standings-card">
                  <h3>Group {groupName}</h3>
                  <StandingsTable>
                    {standings.map((standing, index) => (
                      <tr key={standing.teamName}>
                        <td>{index + 1}</td>
                        <td>{standing.teamName}</td>
                        <td>{standing.played}</td>
                        <td>{standing.wins}</td>
                        <td>{standing.losses}</td>
                        <td>
                          {standing.setsWon}:{standing.setsLost}
                        </td>
                        <td>{standing.pointDifference}</td>
                        <td>{standing.points}</td>
                      </tr>
                    ))}
                  </StandingsTable>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card
        className="matches-section"
        title="Matches"
        actions={
          <Button
            variant={showFilters ? "tint" : "ghost"}
            size="sm"
            icon={<Filter />}
            aria-expanded={showFilters}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
        }
      >
        {showFilters && (
          <div className="filters-panel">
            {uniqueGroups.length > 0 && (
              <Field label="Group" className="filter-group">
                {(id) => (
                  <Select id={id} block value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
                    <option value="all">All Groups</option>
                    {uniqueGroups.map((group) => (
                      <option key={group} value={group}>
                        Group {group}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            {teams.length > 0 && (
              <Field label="Team" className="filter-group">
                {(id) => (
                  <Select id={id} block value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)}>
                    <option value="all">All Teams</option>
                    {teams.map((team) => (
                      <option key={team} value={team}>
                        {team}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            <Field label="Number of Sets" className="filter-group">
              {(id) => (
                <Select id={id} block value={numberOfSets} onChange={(e) => setNumberOfSets(parseInt(e.target.value))}>
                  <option value="1">1 Set</option>
                  <option value="3">3 Sets</option>
                  <option value="5">5 Sets</option>
                </Select>
              )}
            </Field>
            <Button
              variant="ghost"
              className="clear-filters-btn"
              onClick={() => {
                setFilterGroup("all");
                setFilterTeam("all");
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}

        <div className="matches-container">
          {filteredMatches.length > 0 ? (
            filteredMatches.map((match: any, filteredIndex: number) => {
              // Find the original index in the full matches array
              const originalIndex = generatedData.matches.findIndex(
                (m: any) =>
                  m.homeTeam === match.homeTeam &&
                  m.awayTeam === match.awayTeam &&
                  m.round === match.round &&
                  (m.group || "") === (match.group || "")
              );
              const key = `match-${originalIndex}`;
              const score = matchScores[key] || { sets: Array(numberOfSets).fill(null).map(() => ({ homeScore: "", awayScore: "" })) };
              const sets = score.sets || Array(numberOfSets).fill(null).map(() => ({ homeScore: "", awayScore: "" }));

              const homeSetsWon = sets.reduce((setsWon: number, set: SetScore) => {
                if (set.homeScore !== "" && set.awayScore !== "") {
                  const homeScore = Number(set.homeScore);
                  const awayScore = Number(set.awayScore);
                  if (homeScore > awayScore) return setsWon + 1;
                }
                return setsWon;
              }, 0);

              const awaySetsWon = sets.reduce((setsWon: number, set: SetScore) => {
                if (set.homeScore !== "" && set.awayScore !== "") {
                  const homeScore = Number(set.homeScore);
                  const awayScore = Number(set.awayScore);
                  if (awayScore > homeScore) return setsWon + 1;
                }
                return setsWon;
              }, 0);

              return (
                <div key={`${originalIndex}-${filteredIndex}`} className="fast-match-card">
                  <div className="teams-container">
                    <div className="team-container">
                      <p className="team-name">{match.homeTeam}</p>
                      <div className="row">
                        <div className="game-scores">
                          {sets.map((set: SetScore, setIndex: number) => (
                            <div key={setIndex} className="set-input-group">
                              <input
                                type="number"
                                className="game-score-input"
                                min="0"
                                max="100"
                                value={set.homeScore}
                                onChange={(e) => handleScoreChange(originalIndex, setIndex, "home", e.target.value)}
                                placeholder="-"
                                aria-label={`Set ${setIndex + 1}`}
                                data-tooltip={`Set ${setIndex + 1}`}
                              />
                              {sets.length > 1 && (
                                <button
                                  type="button"
                                  className="remove-set-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveSet(originalIndex, setIndex);
                                  }}
                                  aria-label="Remove set"
                                  data-tooltip="Remove set"
                                >
                                  <Minus size={10} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        <p className={`team-score ${homeSetsWon > awaySetsWon ? "bold" : ""}`}>
                          {homeSetsWon || ""}
                        </p>
                      </div>
                    </div>
                    <div className="team-container">
                      <p className="team-name">{match.awayTeam}</p>
                      <div className="row">
                        <div className="game-scores">
                          {sets.map((set: SetScore, setIndex: number) => (
                            <div key={setIndex} className="set-input-group">
                              <input
                                type="number"
                                className="game-score-input"
                                min="0"
                                max="100"
                                value={set.awayScore}
                                onChange={(e) => handleScoreChange(originalIndex, setIndex, "away", e.target.value)}
                                placeholder="-"
                                aria-label={`Set ${setIndex + 1}`}
                                data-tooltip={`Set ${setIndex + 1}`}
                              />
                            </div>
                          ))}
                        </div>
                        <p className={`team-score ${awaySetsWon > homeSetsWon ? "bold" : ""}`}>
                          {awaySetsWon || ""}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="match-actions">
                    <IconButton
                      variant="tint"
                      size="sm"
                      label="Add set"
                      icon={<Plus />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddSet(originalIndex);
                      }}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <EmptyState compact title="No matches found" />
          )}
        </div>
      </Card>
    </div>
  );
};

export default FastTournamentPage;
